import { useInfiniteQuery } from 'react-query'
import { sendToApi } from 'api/client'
import { SUCCESS_RESPONSE_TYPE } from 'api/constants'

interface UseProps {
  query?: string
  pageSize: number
  videoType?: 'all' | 'film' | 'animation'
  category?: string
}

interface FetchProps extends UseProps {
  page: number
}

type VideoParams = {
  url: string
  width: number
  height: number
  mime_type?: string
  thumbnail: string
}

export type StockVideo = {
  id: string
  duration: number
  videos: {
    medium: VideoParams
    tiny: VideoParams
  }
}

export const VIDEO_CATEGORIES = [
  'backgrounds',
  'fashion',
  'nature',
  'science',
  'education',
  'feelings',
  'health',
  'people',
  'places',
  'animals',
  'industry',
  'computer',
  'food',
  'sports',
  'transportation',
  'travel',
  'buildings',
  'business',
  'music',
]

export type StockVideoResponse = {
  hits: StockVideo[]
  total: number
}

const fetchStockVideos = async ({ page, query, pageSize, videoType, category }: FetchProps) => {
  const categoryParam = category ? `&category=${category}` : ''
  const videoTypeParam = videoType ? `&video_type=${videoType}` : ''

  const response = await sendToApi<StockVideoResponse>(
    `/storyblocks/videos?query=${query}&page=${page}&per_page=${pageSize}${videoTypeParam}${categoryParam}`,
  )

  if (response.type !== SUCCESS_RESPONSE_TYPE) {
    throw new Error(response.data)
  }

  return {
    videos: response.data.hits,
    nextPage: response.data.total > pageSize * page ? page + 1 : null,
  }
}

/**
 * Unified hook for stock videos that uses Storyblocks
 *
 */
export const useStockVideos = ({ query, pageSize, videoType, category }: UseProps) => {
  const MINUTE = 1000 * 60
  const isRandom = !query
  const queryOptions = isRandom
    ? {
        cacheTime: MINUTE * 3,
        staleTime: MINUTE * 3,
      }
    : {
        cacheTime: Infinity,
        staleTime: Infinity,
      }

  return useInfiniteQuery<{ videos: StockVideo[]; nextPage: number | null }>(
    ['storyblocks', { query, pageSize, videoType, category }],
    async ({ pageParam = 1 }) => fetchStockVideos({ page: pageParam, query, pageSize, videoType, category }),
    {
      getNextPageParam: (lastPage) => lastPage.nextPage,
      ...queryOptions,
    },
  )
}
